import { FirebaseError } from 'firebase/app';
import { FirestoreError } from 'firebase/firestore';
import { getBytes, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { makeAutoObservable, reaction, runInAction, when } from 'mobx';

import { CloudFunctionsService, FirebaseService } from '@services';
import {
  LanguageOptions,
  LoaderInitializationOptions,
  Partner,
  Logos,
  LegalTerms,
  LegalKeys,
  Program,
} from '@types';
import { sendEventToHost, rollbar } from '@utils';

import { store } from './store';
import { storage } from '../firebase.config';
import i18n from '../i18n/config';

export default class PartnerStore {
  firebaseService = new FirebaseService(new CloudFunctionsService());
  partner?: Partner;
  // Programs for the partner (with savings_rule_id)
  programs: Program[] = [];
  partnerLoading: boolean = true;
  partnerError?: FirestoreError;
  options: LoaderInitializationOptions = {};
  logos?: Logos = {};
  baseUrl: string = import.meta.env.VITE_LOGO_BUCKET_URL;
  whiteLabelEnabled?: boolean;
  legalTerms?: LegalTerms = {
    tcpaEn: '',
    tcpaEs: '',
    copyright: '',
  };

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.partnerError,
      (error) => {
        rollbar.error('Partner store: Error occurred', error);
        sendEventToHost('error', {
          data: {
            error,
          },
        });
      }
    );
  }

  initReactions() {
    when(
      () => !!store?.initializeStore?.token,
      async () => {
        await this.fetchPartner();
      }
    );
  }

  /** Resolves once `programs` is populated (or failed); awaited by the results path. */
  programsReady: Promise<void> = Promise.resolve();

  fetchPrograms = async () => {
    try {
      const token = store.initializeStore.token;
      if (!token) return;

      if (!store.userStore.user) {
        try {
          await when(
            () => !!store.userStore.user || !store.userStore.userLoading,
            { timeout: 10_000 }
          );
        } catch {
          return;
        }
        if (!store.userStore.user) return;
      }

      const result = await this.firebaseService.getRequiredPrograms({
        token,
      });
      this.programs = result?.data ?? [];
    } catch {
      this.programs = [];
    }
  };

  /** True if the given program (by display name) has a Savings Rule configured. */
  hasSavingsRuleForProgram = (programName: string): boolean => {
    const program = this.programs.find(
      (p) => p.name === programName && p.savings_rule_id
    );
    return !!program?.savings_rule_id;
  };

  fetchPartner = async () => {
    try {
      const token = store.initializeStore.token;
      const requestingHost = store.initializeStore.options.requestingHost;

      if (token && requestingHost) {
        const result = await this.firebaseService.getPartnerByToken({
          token,
          requestingHost,
        });

        this.partner = result.data;
        this.setWhiteLabelEnabled(this.partner);

        await Promise.all([
          this.fetchLegal(this.partner?.id),
          this.fetchLogos(),
        ]);
        // Programs are only read on the results screen; load them in the background.
        this.programsReady = this.fetchPrograms();

        this.options = store.initializeStore.options;
        this.partnerLoading = false;
      }
    } catch (error) {
      runInAction(() => {
        this.partnerError = error as FirestoreError;
      });
    }
  };

  fetchDefaultLogos = async () => {
    const isEnglish = i18n.language === 'en';
    const fallbackPath = isEnglish
      ? `default/FullColor_wtagline_64px.svg?updated=${Date.now()}`
      : `default/FullColor_eswtagline_64px.svg?updated=${Date.now()}`;

    this.logos!.headerLogo =
      this.baseUrl + `default/idaLogo.svg?updated=${Date.now()}`;
    this.logos!.whiteLogo =
      this.baseUrl +
      `default/MonoColorWhite_notagline_48px.svg?updated=${Date.now()}`;
    this.logos!.tagLineLogo = this.baseUrl + fallbackPath;
  };

  setWhiteLabelEnabled = async (partnerData: Partner) => {
    this.whiteLabelEnabled = partnerData.whiteLabelToggle;
  };

  fetchLogos = async () => {
    if (
      !this.partner?.Logo_FullColor_notagline_32px ||
      !this.partner?.Logo_FullColor_wtagline_64px ||
      !this.partner?.Logo_MonoColorWhite_notagline_48px ||
      !this.partner?.Logo_FullColor_eswtagline_64px ||
      !this.whiteLabelEnabled
    ) {
      await this.fetchDefaultLogos();
    } else {
      const isEnglish = i18n.language === 'en';
      const partnerLogo = isEnglish
        ? this.partner?.Logo_FullColor_wtagline_64px
        : this.partner?.Logo_FullColor_eswtagline_64px;

      this.logos!.headerLogo = this.partner.Logo_FullColor_notagline_32px;
      this.logos!.tagLineLogo = partnerLogo;
      this.logos!.whiteLogo = this.partner?.Logo_MonoColorWhite_notagline_48px;
    }
  };

  constructFilesToLoad = (
    path: string
  ): { path: string; key: keyof LegalTerms; label: string }[] => {
    return [
      {
        path: `docs/${path}/tcpaDisclosure_SDK_EN.html`,
        key: 'tcpaEn',
        label: 'tcpa english',
      },
      {
        path: `docs/${path}/tcpaDisclosure_SDK_ES.html`,
        key: 'tcpaEs',
        label: 'tcpa spanish',
      },
      {
        path: `docs/${path}/legalName.html`,
        key: 'copyright',
        label: 'copyright',
      },
    ];
  };

  checkPartnerLegal = async (path: string, partnerId: string) => {
    const fileRef = ref(storage, path);

    try {
      await getDownloadURL(fileRef);
      return true;
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        if (error.code === 'storage/object-not-found') {
          const fallbackFileName = `docs/${partnerId}/legalName.html`;
          const fallbackRef = ref(storage, fallbackFileName);

          const htmlContent = `
        <html>
            <p>This is a placeholder legal document for ${partnerId}.</p>
        </html>
      `;

          const blob = new Blob([htmlContent], { type: 'text/html' });
          await uploadBytes(fallbackRef, blob);

          return false;
        }
      }
      throw error;
    }
  };

  fetchLegal = async (partnerId?: string) => {
    let whiteLabelFilesExist = false;

    if (this.whiteLabelEnabled && partnerId) {
      const termsPartnerEn = `docs/${partnerId}/TC_PP_ESign_EN.html`;
      whiteLabelFilesExist = await this.checkPartnerLegal(
        termsPartnerEn,
        partnerId
      );
    }

    const filesToLoad =
      whiteLabelFilesExist && partnerId
        ? this.constructFilesToLoad(partnerId)
        : this.constructFilesToLoad('default');

    const generation = ++this.legalGeneration;

    filesToLoad.forEach(({ path, key }) => {
      this.fetchDoc(path, (response) => {
        if (generation !== this.legalGeneration) return;
        this.legalTerms![key] = response;
      });
    });
  };

  setPartnerOptionLanguage = (language: LanguageOptions) => {
    this.options = {
      ...this.options,
      passthroughParams: {
        ...this.options.passthroughParams,
        language,
      },
    };
  };

  get esignUrls() {
    return {
      en: this.partner?.esign_url,
      es: this.partner?.esign_url_es,
    };
  }

  get privacyPolicyUrls() {
    return {
      en: this.partner?.privacy_url,
      es: this.partner?.privacy_url_es,
    };
  }

  get termsOfUseUrls() {
    return {
      en: this.partner?.terms_url,
      es: this.partner?.terms_url_es,
    };
  }

  get arbitrationUrls() {
    return {
      en: this.partner?.arbitration_url,
      es: this.partner?.arbitration_url_es,
    };
  }

  get tcpaUrls() {
    return {
      [LegalKeys.ESIGN_DISCLOSURE]: this.esignUrls,
      [LegalKeys.TERMS_OF_USE]: this.termsOfUseUrls,
      [LegalKeys.ARBITRATION_AGREEMENT]: this.arbitrationUrls,
      [LegalKeys.PRIVACY_POLICY]: this.privacyPolicyUrls,
    };
  }

  /** Distinct reasons fingerprint as separate Rollbar items instead of one. */
  private logDocFailure = (
    fileName: string,
    reason: string,
    startedAt: number,
    error?: unknown
  ) => {
    rollbar.error(
      `Partner store: Error occurred retrieving file name: ${fileName} (${reason})`,
      (error ?? new Error(reason)) as Error,
      {
        reason,
        elapsedMs: Date.now() - startedAt,
        onLine: navigator.onLine,
        visibility: document.visibilityState,
      }
    );
  };

  /** Bumped per fetchLegal run so a stale reply cannot overwrite a newer partner. */
  private legalGeneration = 0;

  private fetchDoc = async (
    fileName: string,
    handler: (response: string) => void
  ) => {
    const startedAt = Date.now();

    try {
      // getBytes hits ?alt=media directly, so it skips the getDownloadURL
      // metadata call that exhausted its retries in EC-1216, while keeping
      // Firebase's bounded retry (maxOperationRetryTime) and its error codes.
      const bytes = await getBytes(ref(storage, fileName));
      handler(new TextDecoder().decode(bytes));
    } catch (error) {
      const reason = error instanceof FirebaseError ? error.code : 'unknown';
      this.logDocFailure(fileName, reason, startedAt, error);
    }
  };
}
